import { ReceiptProvider } from "../services";
import {
	ReceiptBindingTarget,
	ReceiptSystemProviderEnum,
} from "../services/ReceiptProvider/types";
import { isMultipleRegistersEnabled } from "../services/ReceiptProvider/multipleLegalEntities";

/** Заливка ячейки: `success` — рабочая касса есть, `error` — нет привязанной активной кассы. */
export type ReceiptColumnFill = "success" | "error" | null;

export interface ReceiptColumnInfo {
	/** Есть ли активный провайдер чеков, применимый к филиалу строки и цели привязки. */
	hasProvider: boolean;
	/** Чеки включены для конкретного водителя/машины (провайдер есть + флаг включён). */
	enabled: boolean;
	/** Названия применимых провайдеров (через запятую) — то, что показываем рядом. */
	providerNames: string;
	/**
	 * Цвет фона ячейки (только Checkbox). Не зависит от галки: галка — включена
	 * ли отправка, цвет — есть ли привязанная активная касса. Одна касса —
	 * всегда `success`; несколько касс — `success`, если водитель/машина
	 * привязан к активной кассе, иначе `error` (не привязан или касса выключена).
	 */
	fill: ReceiptColumnFill;
}

/**
 * Вычисляет данные для колонки «Электронные чеки» в таблицах водителей/транспорта.
 *
 * Провайдер не хранит списки водителей/машин — он привязан к филиалу
 * (`taxiServiceIds`) и цели (`bindingTarget`). Поэтому применимость строки
 * определяется по её филиалу, а сам факт включения — по `useElectronicReceipts`.
 */
export function resolveReceiptColumnInfo(
	providers: ReceiptProvider.Model[] | null | undefined,
	taxiServiceId: number | null | undefined,
	bindingTarget: ReceiptBindingTarget,
	useElectronicReceipts: boolean | null | undefined,
	entityId?: number | null,
): ReceiptColumnInfo {
	if (!taxiServiceId || !providers?.length) {
		return {
			hasProvider: false,
			enabled: false,
			providerNames: "",
			fill: null,
		};
	}

	const applicable = providers.filter(
		(provider) =>
			provider?.active &&
			provider.bindingTarget === bindingTarget &&
			(provider.taxiServiceIds ?? []).includes(taxiServiceId),
	);

	const hasProvider = applicable.length > 0;

	return {
		fill: resolveFill(applicable, bindingTarget, entityId),
		hasProvider,
		enabled: hasProvider && useElectronicReceipts === true,
		providerNames: applicable
			.map((provider) => String(provider.name ?? "").trim())
			.filter(Boolean)
			.join(", "),
	};
}

function resolveFill(
	applicable: ReceiptProvider.Model[],
	bindingTarget: ReceiptBindingTarget,
	entityId: number | null | undefined,
): ReceiptColumnFill {
	const checkbox = applicable.find(
		(provider) => provider.provider === ReceiptSystemProviderEnum.CHECKBOX,
	);
	if (!checkbox) return null;
	if (
		!isMultipleRegistersEnabled(checkbox.provider, checkbox.configuration)
	) {
		return "success";
	}
	if (entityId == null) return "error";

	const isCar = bindingTarget === ReceiptBindingTarget.CAR;
	const bound = (checkbox.cashRegisters ?? []).find((register) =>
		(isCar ? register.carIds : register.executorIds)?.includes(entityId),
	);

	return bound && bound.active ? "success" : "error";
}
